import * as THREE from 'three';
import * as CANNON from 'cannon';
import Swal from 'sweetalert2';

import { CameraOperator } from '../core/CameraOperator';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';

import { Detector } from '../../lib/utils/Detector';
import { CannonDebugRenderer } from '../../lib/cannon/CannonDebugRenderer';
import * as _ from 'lodash';

import { InputManager } from '../core/InputManager';
import * as Utils from '../core/FunctionLibrary';
import { LoadingManager } from '../core/LoadingManager';
import { InfoStack } from '../core/InfoStack';
import { IWorldEntity } from '../interfaces/IWorldEntity';
import { IUpdatable } from '../interfaces/IUpdatable';
import { Character } from '../character/Character';
import { Path } from './Path';
import { NavMesh } from './NavMesh';
import { TrimeshCollider } from '../physics/colliders/TrimeshCollider';
import { DynamicCollisionSystem } from '../physics/DynamicCollisionSystem';
import { Vehicle } from '../vehicles/Vehicle';
import { Scenario } from './Scenario';
import { Sky } from './Sky';
import { Ocean } from './Ocean';
import { Vector3 } from 'three';
import { UiManagerService } from '../ui/ui-manager.service';
import { WorldService } from '../ui/word-controller/world.service';
import { PlayerService } from '../character/player.service';
import { NpcSpawnService } from './NpcSpawnService';
import { NpcSpawnPoint } from './NpcSpawnPoint';
import { CrimeDetectionService } from './CrimeDetectionService';
import { WantedLevelService } from './WantedLevelService';
import { CopSpawnService } from './CopSpawnService';
import { GameStateServiceService } from './GameStateService.service';
import { ScoreService } from './score.service';
import { EscapeService } from './EscapeService';
import { GameState } from '../enums/GameState';
import { AudioManager } from '../core/AudioManager';
import { registerAllAudio } from './AudioRegistry';
import { AmbientAudio } from './audio/AmbientAudio';

export class World {
  public renderer: THREE.WebGLRenderer;
  public camera: THREE.PerspectiveCamera;
  public composer: any;
  public graphicsWorld: THREE.Scene;
  public sky: Sky;
  public physicsWorld: CANNON.World;
  public parallelPairs: any[];
  public physicsFrameRate: number;
  public physicsFrameTime: number;
  public physicsMaxPrediction: number;
  public clock: THREE.Clock;
  public renderDelta: number;
  public logicDelta: number;
  public requestDelta: number;
  public sinceLastFrame: number;
  public justRendered: boolean;
  public params: any;
  public inputManager: InputManager;
  public cameraOperator: CameraOperator;
  public timeScaleTarget = 1;
  public console: InfoStack;
  public cannonDebugRenderer: CannonDebugRenderer;
  public scenarios: Scenario[] = [];
  public characters: Character[] = [];
  public vehicles: Vehicle[] = [];
  public paths: Path[] = [];
  public navMesh: NavMesh | null = null;
  public updatables: IUpdatable[] = [];
  public playerHandPos: Vector3;
  public npcPos: Vector3;
  public player: Character;
  public animationClips: THREE.AnimationClip[] = [];
  public uiManager: UiManagerService;
  public npcSpawnService: NpcSpawnService;
  public copSpawnService: CopSpawnService;
  public crimeDetectionService: CrimeDetectionService;
  public isDead = false;
  public navMeshMesh: THREE.Mesh | null = null;
  public dynamicCollisionSystem: DynamicCollisionSystem;
  public audioListener: THREE.AudioListener;
  public audioManager: AudioManager;
  private lastScenarioID: string;
  private greyBoxMaterial = new THREE.MeshLambertMaterial({ color: 0x111111 });
  private originalMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();


  constructor(public worldService: WorldService,
              public uiManagerService: UiManagerService,
              public playerService: PlayerService,
              public wantedLevelService: WantedLevelService,
              public gameStateService: GameStateServiceService,
              public scoreService: ScoreService,
              public escapeService: EscapeService,
              worldScenePath?: string) {
    const scope = this;

    this.crimeDetectionService = new CrimeDetectionService(this.wantedLevelService);

    // WebGL not supported
    if (!Detector.webgl) {
      Swal.fire({
        icon: 'warning',
        title: 'WebGL compatibility',
        text: 'This browser doesn\'t seem to have the required WebGL capabilities. The application may not work correctly.',
        footer: '<a href="https://get.webgl.org/" target="_blank">Click here for more information</a>',
        showConfirmButton: false,
        buttonsStyling: false
      });
    }

    // Renderer
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.generateHTML();

    // Auto window resize
    function onWindowResize(): void {
      scope.camera.aspect = window.innerWidth / window.innerHeight;
      scope.camera.updateProjectionMatrix();
      scope.renderer.setSize(window.innerWidth, window.innerHeight);
      fxaaPass.uniforms['resolution'].value.set(1 / (window.innerWidth * pixelRatio), 1 / (window.innerHeight * pixelRatio));
      scope.composer.setSize(window.innerWidth * pixelRatio, window.innerHeight * pixelRatio);
    }

    window.addEventListener('resize', onWindowResize, false);

    // Three.js scene
    this.graphicsWorld = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 1010);
    this.camera.filmOffset = 6.4;

    // Audio
    this.audioListener = new THREE.AudioListener();
    this.camera.add(this.audioListener);
    this.audioManager = new AudioManager(this.audioListener);
    registerAllAudio(this.audioManager);
    // Camera helper
    const helper = new THREE.CameraHelper(this.camera);
    helper.name = 'cameraHelper';
    helper.visible = false;
    this.graphicsWorld.add(helper);

    // Passes
    const renderPass = new RenderPass(this.graphicsWorld, this.camera);
    const fxaaPass = new ShaderPass(FXAAShader);

    // FXAA
    const pixelRatio = this.renderer.getPixelRatio();
    fxaaPass.material['uniforms'].resolution.value.x = 1 / (window.innerWidth * pixelRatio);
    fxaaPass.material['uniforms'].resolution.value.y = 1 / (window.innerHeight * pixelRatio);

    // Composer
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(renderPass);
    this.composer.addPass(fxaaPass);

    // Physics
    this.physicsWorld = new CANNON.World();
    this.physicsWorld.gravity.set(0, -9.81, 0);
    this.physicsWorld.broadphase = new CANNON.SAPBroadphase(this.physicsWorld);
    this.physicsWorld.solver.iterations = 10;
    this.physicsWorld.allowSleep = true;

    this.dynamicCollisionSystem = new DynamicCollisionSystem(
      this.physicsWorld,
      () => this.characters.map((char) => {
        const pos = new THREE.Vector3();
        char.getWorldPosition(pos);
        return pos;
      })
    );
    this.registerUpdatable(this.dynamicCollisionSystem);

    this.parallelPairs = [];
    this.physicsFrameRate = 60;
    this.physicsFrameTime = 1 / this.physicsFrameRate;
    this.physicsMaxPrediction = this.physicsFrameRate;


    // RenderLoop
    this.clock = new THREE.Clock();
    this.renderDelta = 0;
    this.logicDelta = 0;
    this.sinceLastFrame = 0;
    this.justRendered = false;


    this.params = {
      Pointer_Lock: true,
      Mouse_Sensitivity: 0.3,
      Time_Scale: 1,
      Shadows: true,
      FXAA: true,
      Debug_Physics: false,
      Debug_FPS: false,
      Sun_Elevation: 50,
      Sun_Rotation: 145,
    };

    // Initialization
    this.inputManager = new InputManager(this, this.renderer.domElement);
    this.cameraOperator = new CameraOperator(this, this.camera, this.params.Mouse_Sensitivity);
    this.sky = new Sky(this);
    // Disable Shadows to Debug
    this.sky.csm.lights.forEach((light) => {
      light.castShadow = true;
    });
    this.npcSpawnService = new NpcSpawnService(this);
    this.copSpawnService = new CopSpawnService(this, this.wantedLevelService, this.npcSpawnService);

    this.escapeService.escaped$.subscribe(() => {
      this.scoreService.bankScore();
      this.wantedLevelService.resetWantedLevel();
    });

    this.worldService.spawnNpcRequest$.subscribe(() => {
      this.spawnRandomNpcNearPlayer();
    });

    this.worldService.spawnCopRequest$.subscribe(() => {
      this.copSpawnService.spawnSingleCopNearPlayer();
    });

    this.worldService.restartGameRequest$.subscribe(() => {
      this.restartGame();
    });

    this.worldService.navmeshDebugRequest$.subscribe((enabled) => {
      if (this.navMeshMesh) { this.navMeshMesh.visible = enabled; }
    });


    this.worldService.collisionDebugRequest$.subscribe((enabled) => {
      if (enabled) {
        this.cannonDebugRenderer = new CannonDebugRenderer(this.graphicsWorld, this.physicsWorld);
      } else if (this.cannonDebugRenderer) {
        this.cannonDebugRenderer.clearMeshes();
        this.cannonDebugRenderer = undefined;
      }
      this.params.Debug_Physics = enabled;
      this.characters.forEach((char) => { char.physics.raycastBox.visible = enabled; });
    });

    this.worldService.ragdollDebugRequest$.subscribe((enabled) => {
      if (!this.player) { return; }
      if (enabled) {
        const excluded = this.worldService.ragdollDisabledBones.size > 0
          ? this.worldService.ragdollDisabledBones
          : undefined;
        this.player.enableRagdoll(excluded);
      } else {
        this.player.disableRagdoll();
      }
    });

    this.gameStateService.playerDied$.subscribe(() => {
      this.gameStateService.gameState$.next(GameState.Wasted);
      this.audioManager.mute();
      setTimeout(() => {
        this.isDead = true;
        document.exitPointerLock();
      }, 1500);
    });

    // Load scene if path is supplied
    if (worldScenePath !== undefined) {
      const loadingManager = new LoadingManager(this, this.uiManagerService);
      loadingManager.onFinishedCallback = () => {
        this.update(1, 1);
        this.setTimeScale(1);
        new AmbientAudio(this.audioManager).play();
      };
      loadingManager.loadGLTF('../assets/animations/animations.glb', (animGltf) => {
        this.animationClips = animGltf.animations;
        loadingManager.loadGLTF(worldScenePath, (gltf) => {
          this.loadScene(loadingManager, gltf);
        });
      });
      this.audioManager.preload((src, onLoaded) => loadingManager.loadAudio(src, onLoaded));
    } else {
    }

    this.render(this);
  }

  // Update
  // Handles all logic updates.
  public update(timeStep: number, unscaledTimeStep: number): void {
    if (this.isDead) { return; }
    this.updatePhysics(timeStep);
    this.params.Time_Scale = this.worldService.worldTimeScale;
    // Update registred objects
    this.updatables.forEach((entity) => {
      entity.update(timeStep, unscaledTimeStep);
    });
    this.escapeService.tick(timeStep, this.copSpawnService.anyPursuerHasLoS);

    // Lerp time scale
    // this.params.Time_Scale = THREE.MathUtils.lerp(this.params.Time_Scale, this.timeScaleTarget, 0.2);

    // Physics debug
    if (this.params.Debug_Physics) { this.cannonDebugRenderer.update(); }
  }

  public updatePhysics(timeStep: number): void {
    // Step the physics world
    this.physicsWorld.step(this.physicsFrameTime, timeStep);

  }

  /**
   * Rendering loop.
   * Implements fps limiter and frame-skipping
   * Calls world's "update" function before rendering.
   * @param {World} world
   */
  public render(world: World): void {
    this.requestDelta = this.clock.getDelta();

    requestAnimationFrame(() => {
      world.render(world);
    });

    // Getting timeStep
    const unscaledTimeStep = (this.requestDelta + this.renderDelta + this.logicDelta);
    let timeStep = unscaledTimeStep * this.params.Time_Scale;
    timeStep = Math.min(timeStep, 1 / 30);    // min 30 fps

    // Logic
    world.update(timeStep, unscaledTimeStep);

    // Measuring logic time
    this.logicDelta = this.clock.getDelta();

    // Frame limiting
    const interval = 1 / 60;
    this.sinceLastFrame += this.requestDelta + this.renderDelta + this.logicDelta;
    this.sinceLastFrame %= interval;


    // Actual rendering with a FXAA ON/OFF switch
    if (this.params.FXAA) { this.composer.render(); }
    else { this.renderer.render(this.graphicsWorld, this.camera); }

    // Measuring render time
    this.renderDelta = this.clock.getDelta();
  }

  public setTimeScale(value: number): void {
    this.params.Time_Scale = value;
    this.timeScaleTarget = value;
  }

  public restartGame(): void {
    this.isDead = false;
    this.inputManager.enable();
    this.scoreService.fullReset();
    this.gameStateService.reset();
    this.audioManager.unmute();
    this.restartScenario();
  }

  public add(worldEntity: IWorldEntity): void {
    worldEntity.addToWorld(this);
    this.registerUpdatable(worldEntity);
  }

  public registerUpdatable(registree: IUpdatable): void {
    this.updatables.push(registree);
    this.updatables.sort((a, b) => (a.updateOrder > b.updateOrder) ? 1 : -1);
  }

  public remove(worldEntity: IWorldEntity): void {
    worldEntity.removeFromWorld(this);
    this.unregisterUpdatable(worldEntity);
  }

  public unregisterUpdatable(registree: IUpdatable): void {
    _.pull(this.updatables, registree);
  }

  public loadScene(loadingManager: LoadingManager, gltf: any): void {
    gltf.scene.traverse((child) => {
      if (child.hasOwnProperty('userData')) {
        if (child.type === 'Mesh') {
          Utils.setupMeshProperties(child);
          this.sky.csm.setupMaterial(child.material);

          if (child.material.name === 'ocean') {
            this.registerUpdatable(new Ocean(child, this));
          }
        }

        if (child.userData.hasOwnProperty('data')) {
          if (child.userData.data === 'physics') {
            if (child.userData.hasOwnProperty('type')) {
              // Convex doesn't work! Stick to boxes!
              if (child.userData.type === 'box') {
                this.dynamicCollisionSystem.registerCollider(
                  Utils.cannonVector(child.position),
                  new THREE.Vector3(child.scale.x, child.scale.y, child.scale.z),
                  Utils.cannonQuat(child.quaternion)
                );
              } else if (child.userData.type === 'trimesh') {
                if (child.userData.entity === 'floor') {
                  const phys = new TrimeshCollider(child, {});
                  this.physicsWorld.addBody(phys.body);
                } else {
                  this.dynamicCollisionSystem.registerTrimeshCollider(child);
                }
              }

              child.visible = false;
            }
          }

          if (child.userData.data === 'path') {
            this.paths.push(new Path(child));
          }

          if (child.userData.data === 'navigation') {
            this.navMesh = new NavMesh(child as THREE.Mesh);
            this.navMeshMesh = child as THREE.Mesh;
            child.visible = false;
          }

          if (child.userData.data === 'scenario') {
            this.scenarios.push(new Scenario(child, this));
          }
        }
      }
    });

    this.graphicsWorld.add(gltf.scene);

    if (this.worldService.greyBoxModeEnabled) {
      gltf.scene.traverse((obj: any) => {
        if (obj.type !== 'Mesh') { return; }
        this.originalMaterials.set(obj, obj.material);
        obj.material = this.greyBoxMaterial;
      });
    }

    // Launch default scenario
    let defaultScenarioID: string;
    for (const scenario of this.scenarios) {
      if (scenario.default) {
        defaultScenarioID = scenario.id;
        break;
      }
    }
    if (defaultScenarioID !== undefined) { this.launchScenario(defaultScenarioID, loadingManager); }
  }

  public launchScenario(scenarioID: string, loadingManager?: LoadingManager): void {
    this.lastScenarioID = scenarioID;

    this.clearEntities();
    this.wantedLevelService.reset();

    // Launch default scenario
    if (!loadingManager) { loadingManager = new LoadingManager(this, this.uiManagerService); }
    for (const scenario of this.scenarios) {
      if (scenario.id === scenarioID || scenario.spawnAlways) {
        scenario.launch(loadingManager, this);
      }
    }

    this.spawnInitialNpcs();
  }

  public restartScenario(): void {
    if (this.lastScenarioID !== undefined) {
      document.exitPointerLock();
      this.launchScenario(this.lastScenarioID);
    } else {
      console.warn('Can\'t restart scenario. Last scenarioID is undefined.');
    }
  }

  public spawnNpcAt(position: THREE.Vector3): void {
    const spawnPoint = new NpcSpawnPoint(position, this.npcSpawnService);
    spawnPoint.spawn(this);
  }

  public spawnRandomNpcNearPlayer(): void {
    if (!this.player) { return; }
    let position: THREE.Vector3;
    if (this.navMesh) {
      position = this.navMesh.getRandomSpawnPosition();
    } else {
      position = new THREE.Vector3();
      this.player.getWorldPosition(position);
      position.add(new THREE.Vector3(3, 0, 0));
    }
    this.spawnNpcAt(position);
  }

  private static readonly INITIAL_PED_COUNT = 7;

  private spawnInitialNpcs(): void {
    if (!this.navMesh) { return; }
    for (let i = 0; i < World.INITIAL_PED_COUNT; i++) {
      this.spawnNpcAt(this.navMesh.getRandomSpawnPosition());
    }
  }


  public clearEntities(): void {
    this.copSpawnService.clearActiveUnits();

    for (let i = 0; i < this.characters.length; i++) {
      this.remove(this.characters[i]);
      i--;
    }

    for (let i = 0; i < this.vehicles.length; i++) {
      this.remove(this.vehicles[i]);
      i--;
    }


  }

  public scrollTheTimeScale(scrollAmount: number): void {
    // Changing time scale with scroll wheel
    const timeScaleBottomLimit = 0.003;
    const timeScaleChangeSpeed = 1.3;

    if (scrollAmount > 0) {
      this.timeScaleTarget /= timeScaleChangeSpeed;
      if (this.timeScaleTarget < timeScaleBottomLimit) { this.timeScaleTarget = 0; }
    } else {
      this.timeScaleTarget *= timeScaleChangeSpeed;
      if (this.timeScaleTarget < timeScaleBottomLimit) { this.timeScaleTarget = timeScaleBottomLimit; }
      this.timeScaleTarget = Math.min(this.timeScaleTarget, 1);
    }
  }

  public initDefaultControls(): void {
    this.uiManagerService.initDefaultControls();
  }

  public updateControls(controls: any): void {
    this.uiManagerService.updateControls(controls);
  }

  private generateHTML(): void {
    // Canvas
    document.body.appendChild(this.renderer.domElement);
    this.renderer.domElement.id = 'canvas';
  }

}
